import React, { forwardRef } from "react";
import { getThermalReceiptStyles } from "./thermalReceiptStyles";
import { ThermalShopHeader, DashedRule, getShopHeaderProps } from "./ThermalShopHeader";
import { formatDateTime } from "../../utils/dateTime";

const formatNum = (n) =>
  Number(n || 0).toLocaleString("en-PK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatRs = (n) =>
  `Rs ${Number(n || 0).toLocaleString("en-PK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const FooterRow = ({ label, value }) => {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="tr-pay-row">
      <span>{label}</span>
      <span className="tr-pay-value">{value}</span>
    </div>
  );
};

const ThermalItemSummary = forwardRef(({ data }, ref) => {
  if (!data) return null;

  const {
    title = "Item Summary",
    partyLabel = "Customer",
    partyName = "",
    partyContact = "",
    partyAddress = "",
    tabLabel = "All",
    rows = [],
    footer = {},
    date,
    showPrice = false,
  } = data;

  const colCount = showPrice ? 4 : 3;
  const productColWidth = showPrice ? "48%" : "58%";

  const shop = getShopHeaderProps(data);
  const formattedDate = date
    ? formatDateTime(date)
    : formatDateTime(new Date());

  const headerRight = (
    <div className="tr-header-right">
      <div className="tr-header-right-line">
        <span>Tab:</span> {tabLabel}
      </div>
    </div>
  );

  const partyParts = [partyName, partyContact, partyAddress].filter(Boolean);
  const partySummary = partyParts.length ? partyParts.join(" - ") : "—";

  return (
    <div ref={ref} className="thermal-receipt">
      <style>{`
        ${getThermalReceiptStyles()}
        .tr-summary-title {
          text-align: center;
          font-size: 12px;
          font-weight: 700;
          margin: 4px 0;
          letter-spacing: 0.2px;
        }
        .tr-table tr.tr-total-row td {
          font-weight: 700;
          background: #f0f0f0;
        }
      `}</style>

      <ThermalShopHeader
        formattedDate={formattedDate}
        headerRight={headerRight}
        shop={shop}
      />

      <div className="tr-summary-title">▶ {title}</div>

      <div className="tr-customer-row">
        <div className="tr-customer-label">
          <span>👤</span>
          <span>{partyLabel}:</span>
        </div>
        <div className="tr-customer-value">{partySummary}</div>
      </div>

      <div className="tr-solid-thick" />

      <table className="tr-table">
        <thead>
          <tr>
            <th style={{ width: productColWidth }}>Item</th>
            {showPrice && <th>Price</th>}
            <th>Qty</th>
            <th>Amt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={`${row.name}-${i}`}
              className={row.isTotal ? "tr-total-row" : ""}
            >
              <td className="tr-product">{row.name}</td>
              {showPrice && (
                <td className="tr-num">{formatNum(row.price)}</td>
              )}
              <td className="tr-num">{formatNum(row.qty)}</td>
              <td className="tr-num">{formatNum(row.amount)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={colCount} style={{ textAlign: "center" }}>
                No items
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <DashedRule />

      {footer.totalBox != null && (
        <FooterRow label="Total Box:" value={formatNum(footer.totalBox)} />
      )}
      {footer.totalShopping != null && (
        <FooterRow
          label="Total Shopping:"
          value={formatNum(footer.totalShopping)}
        />
      )}
      <FooterRow label="Total Amount:" value={formatRs(footer.totalAmount)} />
      <FooterRow label="Opening Balance:" value={formatRs(footer.openingBalance)} />
      <FooterRow label="Discount:" value={formatRs(footer.discount)} />
      <FooterRow label="Return:" value={formatRs(footer.returnAmount)} />
      <FooterRow label="Paid:" value={formatRs(footer.paid)} />
      <FooterRow
        label="Remaining:"
        value={formatRs(footer.remaining)}
      />

      <DashedRule />
      <div className="tr-thanks">★ Thank You For Your Business ★</div>
    </div>
  );
});

ThermalItemSummary.displayName = "ThermalItemSummary";

export default ThermalItemSummary;
